import {
  ChevronRight,
  Palette,
  Globe,
  Shield,
  BarChart,
  Zap,
  Server,
  Users,
  ExternalLink,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "../../i18n";
import CTASection from "../../components/cta-section";
import AppFeaturesShowcase from "../../components/web-app-features-showcase";
import MacbookSafariFrame from "../../components/macbook-safari-frame";
import CompatibilitySection from "../../components/compatibility-section";
import BookingFlow from "../../components/booking-flow";
import OEBB from "./assets/OEBB.jpg";
import EMZ from "./assets/EMZ.png";
import Fietshanger from "./assets/Fietshangar.png";
import Greenguard from "./assets/Greenguard.png";
import Ranko from "./assets/Ranko.png";
import Velodepo from "./assets/Velodepo.png";
import phone1 from "./assets/phone1.png";
import phone2 from "./assets/phone2.png";
import phone3 from "./assets/phone3.png";

export default function Home() {
  const { t } = useTranslation();
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeCustomInterface, setActiveCustomInterface] = useState(0);
  // Auto-rotate custom interfaces
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCustomInterface((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const customInterfaces = [
    {
      id: 0,
      title: t("routes.webApp.customInterfaces.0.title"),
      description: t("routes.webApp.customInterfaces.0.description"),
      image: phone1,
      color: "#8B5CF6",
      features: [
        t("routes.webApp.customInterfaces.0.features.0"),
        t("routes.webApp.customInterfaces.0.features.1"),
        t("routes.webApp.customInterfaces.0.features.2"),
        t("routes.webApp.customInterfaces.0.features.3"),
      ],
    },
    {
      id: 1,
      description: t("routes.webApp.customInterfaces.1.description"),
      title: t("routes.webApp.customInterfaces.1.title"),
      image: phone2,
      color: "#10B981",
      features: [
        t("routes.webApp.customInterfaces.1.features.0"),
        t("routes.webApp.customInterfaces.1.features.1"),
        t("routes.webApp.customInterfaces.1.features.2"),
        t("routes.webApp.customInterfaces.1.features.3"),
      ],
    },
    {
      id: 2,
      title: t("routes.webApp.customInterfaces.2.title"),
      description: t("routes.webApp.customInterfaces.2.description"),
      image: phone3,
      color: "#EF4444",
      features: [
        t("routes.webApp.customInterfaces.2.features.0"),
        t("routes.webApp.customInterfaces.2.features.1"),
        t("routes.webApp.customInterfaces.2.features.2"),
        t("routes.webApp.customInterfaces.2.features.3"),
      ],
    },
  ];

  const features = [
    {
      id: 0,
      title: "Mobility",
      description:
        "Comprehensive mobility solutions for urban transportation needs.",
      benefits: [
        "Seamless integration with public transport",
        "Real-time tracking and notifications",
        "Flexible payment options",
        "Multi-modal journey planning",
      ],
    },
    {
      id: 1,
      title: "Infrastructure",
      description: "Smart infrastructure management for cities and businesses.",
      benefits: [
        "IoT-enabled monitoring systems",
        "Predictive maintenance",
        "Resource optimization",
        "Environmental impact tracking",
      ],
    },
    {
      id: 2,
      title: "Utilities",
      description: "Efficient utility management solutions for modern needs.",
      benefits: [
        "Smart metering integration",
        "Usage analytics and insights",
        "Automated billing systems",
        "Consumption optimization",
      ],
    },
    {
      id: 3,
      title: "Payments",
      description: "Secure and flexible payment processing for all services.",
      benefits: [
        "Multiple payment methods",
        "Subscription management",
        "Automated invoicing",
        "Revenue sharing capabilities",
      ],
    },
    {
      id: 4,
      title: "Analytics",
      description: "Powerful analytics tools to drive business decisions.",
      benefits: [
        "Real-time dashboards",
        "Custom reporting",
        "Predictive analytics",
        "User behavior insights",
      ],
    },
    {
      id: 5,
      title: "Integration",
      description:
        "Seamless integration with existing systems and third-party services.",
      benefits: [
        "Open API architecture",
        "Custom connectors",
        "Legacy system compatibility",
        "Plug-and-play modules",
      ],
    },
    {
      id: 6,
      title: "Security",
      description: "Enterprise-grade security for all platform components.",
      benefits: [
        "End-to-end encryption",
        "Multi-factor authentication",
        "Regular security audits",
        "Compliance with global standards",
      ],
    },
  ];

  const webAppFeatures = [
    {
      icon: <Globe className="h-5 w-5 text-blue-600" />,
      title: t("routes.webApp.webAppFeatures.0.title"),
      description: t("routes.webApp.webAppFeatures.0.description"),
    },
    {
      icon: <Shield className="h-5 w-5 text-green-600" />,
      title: t("routes.webApp.webAppFeatures.1.title"),
      description: t("routes.webApp.webAppFeatures.1.description"),
    },
    {
      icon: <BarChart className="h-5 w-5 text-purple-600" />,
      title: t("routes.webApp.webAppFeatures.2.title"),
      description: t("routes.webApp.webAppFeatures.2.description"),
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden relative">
      {/* Top Gradient Background (only at top) */}

      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pt-32 pb-24 ">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="">
            <h1 className="text-4xl md:text-6xl font-bold text-navy-900 leading-tight">
              {t("routes.webApp.heroTitle")} <br />
              <span className="bg-gradient-to-r from-purple-600 to-orange-400 text-transparent bg-clip-text relative">
                <span className="animate-gradient-x">
                  {t("routes.webApp.heroHighlight")}
                </span>
              </span>
            </h1>
            <p className="mt-6 text-gray-700 text-lg">
              {t("routes.webApp.heroDescription")}
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {webAppFeatures.map((feature, index) => (
                <div key={index} className="flex items-center">
                  <div className="h-10 w-10 flex-shrink-0 mr-3 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 flex items-center justify-center shadow-sm">
                    {feature.icon}
                  </div>
                  <div>
                    <span className="text-sm font-medium">{feature.title}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="  ">
            <div className="">
              <MacbookSafariFrame />
            </div>
          </div>
        </div>

        {/* Web Platform Description Section */}
        <div className="mt-32 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
              {t("routes.webApp.webPlatformTag")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 ">
              {t("routes.webApp.webPlatformTitle")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("routes.webApp.webPlatformDescription")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature Cards */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow border border-blue-100">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-md">
                <Server className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                {t("routes.webApp.featureCard1Title")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("routes.webApp.featureCard1Description")}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    {t("routes.webApp.featureCard1List.0")}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    {t("routes.webApp.featureCard1List.1")}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    {t("routes.webApp.featureCard1List.2")}
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow border border-purple-100">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-md">
                <Zap className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                {t("routes.webApp.featureCard2Title")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("routes.webApp.featureCard2Description")}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    {t("routes.webApp.featureCard2List.0")}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    {t("routes.webApp.featureCard2List.1")}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    {t("routes.webApp.featureCard2List.2")}
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow border border-green-100">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-md">
                <Users className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                {t("routes.webApp.featureCard3Title")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("routes.webApp.featureCard3Description")}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    {t("routes.webApp.featureCard3List.0")}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    {t("routes.webApp.featureCard3List.1")}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-2 mt-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-700">
                    {t("routes.webApp.featureCard3List.2")}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-32 max-w-7xl mx-auto">
          {/* Compatibility Section */}
          <CompatibilitySection />
        </div>

        {/* Booking Flow Section */}
        <div className="mt-32 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 text-blue-800 rounded-full text-sm font-medium mb-4">
              {t("routes.webApp.userExperienceTag")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text">
              {t("routes.webApp.bookingTitle")}
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              {t("routes.webApp.bookingDescription")}
            </p>
          </div>
          <BookingFlow />
        </div>

        {/* App Features Showcase - Comprehensive Platform */}
        <div className="mt-32 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
              {t("routes.webApp.featuresTag")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text">
              {t("routes.webApp.featuresTitle")}
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              {t("routes.webApp.featuresDescription")}
            </p>
          </div>

          <AppFeaturesShowcase />
        </div>

        {/* App Showcase Section - REDESIGNED */}
        <div className="mt-32 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
              {t("routes.webApp.customInterfaceTag")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text">
              {t("routes.webApp.customInterfaceTitle")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("routes.webApp.customInterfaceDescription")}
            </p>
          </div>

          <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12 shadow-lg">
            {/* Grid layout for text and images */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left side: Content */}
              <div>
                <div className="mb-8">
                  <div
                    className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 shadow-md"
                    style={{
                      background: `linear-gradient(to right, ${customInterfaces[activeCustomInterface].color}, ${customInterfaces[activeCustomInterface].color}CC)`,
                    }}
                  >
                    <Palette className="h-6 w-6 text-white" />
                  </div>

                  <h3
                    className="text-2xl font-bold mb-2"
                    key={`title-${activeCustomInterface}`}
                  >
                    {customInterfaces[activeCustomInterface].title}
                  </h3>
                  <p
                    className="text-gray-600 mb-6"
                    key={`desc-${activeCustomInterface}`}
                  >
                    {customInterfaces[activeCustomInterface].description}
                  </p>

                  <ul className="space-y-3">
                    {customInterfaces[activeCustomInterface].features.map(
                      (feature, idx) => (
                        <li
                          key={`feature-${activeCustomInterface}-${idx}`}
                          className="flex items-start"
                        >
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mt-1 mr-3">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="3"
                                d="M5 13l4 4L19 7"
                              ></path>
                            </svg>
                          </div>
                          <span>{feature}</span>
                        </li>
                      )
                    )}
                  </ul>
                </div>

                <div className="flex space-x-2 mt-8">
                  {customInterfaces.map((_, idx) => (
                    <button
                      key={`dot-${idx}`}
                      onClick={() => setActiveCustomInterface(idx)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        activeCustomInterface === idx
                          ? "bg-blue-600 w-8"
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                      aria-label={`${t("routes.webApp.viewCustomInterface")} ${
                        idx + 1
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Right side: Interface screenshots */}
              <div className="flex justify-center">
                {customInterfaces.map((interface_, idx) => (
                  <div
                    key={`interface-${idx}`}
                    className={`transition-all duration-500 ease-in-out ${
                      idx === activeCustomInterface
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-95 absolute"
                    }`}
                  >
                    <img
                      src={interface_.image || "/placeholder.svg"}
                      alt={`Custom Interface ${idx + 1}`}
                      className="rounded-xl max-h-[600px]"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-12">
              <a
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                href="#contact"
              >
                {t("routes.webApp.becomePartner")}
              </a>
              <p className="mt-4 text-gray-500">
                {t("routes.webApp.partnerDescription")}
              </p>
            </div>
          </div>
        </div>

        {/* Live Demo Section */}
        <div className="mt-32 max-w-7xl mx-auto">
          <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl overflow-hidden">
            {/* Dot pattern overlay */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            ></div>

            {/* Content container */}
            <div className="relative z-10 px-6 py-16 md:px-12 lg:px-16">
              <div className="text-center mb-8">
                <span className="inline-block px-4 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-4">
                  {t("routes.webApp.liveDemoTag")}
                </span>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                  {t("routes.webApp.liveDemoTitle")}
                </h2>
                <p className="text-xl text-white/80 max-w-2xl mx-auto">
                  {t("routes.webApp.liveDemoDescription")}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-stretch">
                {/* Left side: Stats and info */}
                <div className="text-white">
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-2">
                      {t("routes.webApp.mapHeading")}
                    </h3>
                    <p className="text-white/80 mb-4">
                      {t("routes.webApp.mapDescription")}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold mb-1">100+</div>
                      <div className="text-sm text-white/70">
                        {t("routes.webApp.statProviders")}
                      </div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold mb-1">3 min</div>
                      <div className="text-sm text-white/70">
                        {t("routes.webApp.statBookingTime")}
                      </div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold mb-1">24/7</div>
                      <div className="text-sm text-white/70">
                        {t("routes.webApp.statAvailability")}
                      </div>
                    </div>
                  </div>

                  {/* Partner logos carousel */}
                  <div className="mb-8">
                    <p className="text-white/80 mb-4 text-center font-medium">
                      {t("routes.webApp.partners")}
                    </p>
                    <div className="flex overflow-scroll space-x-8 partner-logo-carousel">
                      <div className="flex space-x-8 animate-carousel">
                        <div className="w-24 h-12 bg-white backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <img
                            src={OEBB}
                            alt="ÖBB"
                            className="h-8 px-2 bg-object-contain"
                          />
                        </div>
                        <div className="w-24 h-12 bg-white backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <img
                            src={EMZ}
                            alt="EMZ"
                            className="h-8 px-2 bg-object-contain"
                          />
                        </div>
                        <div className="w-24 h-12 bg-white backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <img
                            src={Fietshanger}
                            alt="Fietshanger"
                            className="h-8 px-2 bg-object-contain"
                          />
                        </div>
                        <div className="w-24 h-12 bg-white backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <img
                            src={Greenguard}
                            alt="Greenguard"
                            className="h-8 px-2 bg-object-contain"
                          />
                        </div>
                        <div className="w-24 h-12 bg-white backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <img
                            src={Ranko}
                            alt="Ranko"
                            className="h-8 px-2 bg-object-contain object-contain
"
                          />
                        </div>
                        <div className="w-24 h-12 bg-white backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <img
                            src={Velodepo}
                            alt="Velodepo"
                            className="h-8 px-2 bg-object-contain object-contain
"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-4">
                    <a
                      className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-white/90 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-200 group"
                      href="#contact"
                    >
                      <span className="flex items-center justify-center">
                        <span className="mr-2">
                          {t("routes.webApp.joinOperator")}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    </a>

                    {/* Launch Web App Button */}
                    <a
                      href="https://app.juhuu.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center bg-blue-800 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-900 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-200"
                    >
                      {t("routes.webApp.openWebApp")}
                      <ExternalLink className="ml-2 h-5 w-5" />
                    </a>
                  </div>
                </div>

                {/* Right side: MacBook with Safari */}
                <div className="w-full h-auto">
                  <MacbookSafariFrame />
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2"></div>
          </div>
        </div>
      </main>

      {/* CTA Section */}
      <div id="contact">
        <CTASection />
      </div>
    </div>
  );
}
